import React from "react";
import { useLang } from "./LanguageProvider";
import { MapPin, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function GccFootprint() {
  const { t, isRtl } = useLang();
  const Arrow = isRtl ? ArrowLeft : ArrowRight;
  const g = t.gcc;

  return (
    <section
      id="gcc"
      data-testid="gcc-section"
      className="relative bg-brand-navy py-24 md:py-40 border-t border-white/5 overflow-hidden"
    >
      <div className="container mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20">
          <div className="lg:col-span-6 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-brand-teal" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-brand-teal font-semibold">
                {g.overline}
              </span>
            </div>
            <h2 className="font-heading text-white text-2xl sm:text-3xl md:text-4xl lg:text-[42px] leading-[1.15] font-medium tracking-tight">
              {g.title}
            </h2>
          </div>
          <div className="lg:col-span-6 reveal reveal-delay-1">
            <ul className={`space-y-4 ${isRtl ? "text-right" : "text-left"}`}>
              {g.leadPoints.map((point, i) => (
                <li
                  key={i}
                  data-testid={`gcc-lead-point-${i}`}
                  className="flex gap-3 text-base md:text-lg text-white/70 leading-relaxed"
                >
                  <span
                    aria-hidden="true"
                    className="mt-2.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-teal"
                  />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Journey strip — US -> Qatar -> UAE */}
        <div
          data-testid="gcc-journey"
          className="reveal reveal-delay-1 mb-12 md:mb-16 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch gap-4 md:gap-6 rounded-xl border border-white/10 bg-brand-navyDeep/60 p-6 md:p-8"
        >
          {g.journey.map((node, i) => (
            <React.Fragment key={i}>
              <div
                data-testid={`gcc-journey-${i}`}
                className="flex flex-col gap-1.5"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-teal font-semibold">
                  {node.tag}
                </div>
                <div className="text-white text-base md:text-lg font-medium leading-tight">
                  {node.label}
                </div>
                <div dir="ltr" className={`text-white/50 text-xs md:text-sm ${isRtl ? "text-right" : ""}`}>
                  {node.year}
                </div>
              </div>
              {i < g.journey.length - 1 && (
                <div className="hidden md:flex items-center justify-center text-white/30">
                  <Arrow size={18} strokeWidth={1.5} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Country cards — Qatar (100%) | UAE (50%) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {/* Qatar */}
          <article
            data-testid="gcc-card-qatar"
            className="reveal reveal-delay-2 relative rounded-xl border border-white/10 bg-brand-navyDeep p-7 md:p-10 overflow-hidden"
          >
            <div className="flex items-center gap-2.5 text-white/85 mb-6">
              <MapPin size={14} className="text-brand-teal" strokeWidth={2} />
              <span className="text-[11px] uppercase tracking-[0.22em] font-semibold">
                {g.qatar.tag}
              </span>
            </div>

            <div className="flex items-baseline gap-3 mb-1.5">
              <span dir="ltr" data-testid="gcc-stat-qatar" className="display-num text-white text-5xl md:text-6xl">
                {g.qatar.stat}
              </span>
              <span className="text-white/55 text-sm md:text-base">{g.qatar.statLabel}</span>
            </div>

            {/* full progress bar */}
            <div className="mt-4 mb-6 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
              <div
                dir="ltr"
                className="h-full bg-brand-teal rounded-full"
                style={{ width: "100%" }}
              />
            </div>

            <p className={`text-white/70 text-sm md:text-base leading-relaxed ${isRtl ? "text-right" : "text-left"}`}>
              {g.qatar.body}
            </p>

            <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-brand-teal font-semibold border border-brand-teal/40 rounded-full px-3 py-1.5">
              <CheckCircle2 size={12} strokeWidth={2.5} />
              {g.qatar.chip}
            </div>
          </article>

          {/* UAE */}
          <article
            data-testid="gcc-card-uae"
            className="reveal reveal-delay-3 relative rounded-xl border border-white/10 bg-brand-navyDeep p-7 md:p-10 overflow-hidden"
          >
            <div className="flex items-center gap-2.5 text-white/85 mb-6">
              <MapPin size={14} className="text-brand-teal" strokeWidth={2} />
              <span className="text-[11px] uppercase tracking-[0.22em] font-semibold">
                {g.uae.tag}
              </span>
            </div>

            <div className="flex items-baseline gap-3 mb-1.5">
              <span dir="ltr" data-testid="gcc-stat-uae" className="display-num text-white text-5xl md:text-6xl">
                {g.uae.stat}
              </span>
              <span className="text-white/55 text-sm md:text-base">{g.uae.statLabel}</span>
            </div>

            {/* half progress bar */}
            <div className="mt-4 mb-6 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
              <div
                dir="ltr"
                className="h-full bg-brand-teal rounded-full"
                style={{ width: "50%" }}
              />
            </div>

            <p className={`text-white/70 text-sm md:text-base leading-relaxed ${isRtl ? "text-right" : "text-left"}`}>
              {g.uae.body}
            </p>

            <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/70 font-semibold border border-white/20 rounded-full px-3 py-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-teal animate-pulse" />
              {g.uae.chip}
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
